import { type FC, PropsWithChildren } from 'react';
import { styled } from 'styled-components';

import { QueryState } from '@/api/components/QueryState';
import { useFetchFundHoldingsQuery } from '@/api/fundHoldings/fundHoldingsApi';
import { useOrganizationName } from '@/api/organizations/hooks/useOrganizationName';
import { usePortfoliosQuery } from '@/api/portfolios';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { isNotReady } from '@/api/utils/isNotReady';
import { AnnouncementWarning } from '@/components/AnnouncementWarning';
import { CenteredPageContent } from '@/components/CenteredPageContent';
import { MainContent } from '@/components/MainContent';
import { OrderToSignAlert } from '@/components/OrderToSignAlert/OrderToSignAlert';
import { PageHeading } from '@/components/PageHeading';
import { WithGap } from '@/components/WithGap';
import { prettifyOrganizationName } from '@/util/prettifyOrganizationName';

import { AnnouncementInfo } from './components/AnnouncementInfo';
import { findPreferredPortfolioGroupId } from './components/findPreferredPortfolioGroupId';
import { NewsArticlesWidget } from './components/NewsArticlesWidget';
import { NoInvestmentsMessage } from './components/NoInvestmentsMessage';
import { NoPortfoliosDashboard } from './components/NoPortfoliosDashboard';
import { PortfolioDashboard } from './components/PortfolioDashboard';

export const DashboardPage: FC = () => {
  const portfoliosQuery = usePortfoliosQuery();
  const holdingsQuery = useFetchFundHoldingsQuery();

  const isHoldingsLoading = isNotReady(holdingsQuery, {
    skipError: true,
    skipNoData: true,
  });

  const hasPortfolios =
    (portfoliosQuery.data?.portfolioGroups?.length ?? 0) > 0;

  if (isNotReady(portfoliosQuery)) {
    return (
      <DashboardPageContent>
        <QueryState query={portfoliosQuery} />
      </DashboardPageContent>
    );
  }

  if (isHoldingsLoading) {
    return (
      <DashboardPageContent>
        <QueryState query={holdingsQuery} />
      </DashboardPageContent>
    );
  }

  if (!hasPortfolios) {
    return (
      <DashboardPageContent>
        <NoPortfoliosDashboard />
      </DashboardPageContent>
    );
  }

  const preferredPortfolioGroupId = findPreferredPortfolioGroupId({
    portfolios: portfoliosQuery.data!,
    holdings: holdingsQuery.data,
  });

  // Check if we have performance data.
  const preferredGroup = portfoliosQuery.data!.portfolioGroupById.get(
    preferredPortfolioGroupId,
  );
  const hasPerformanceData =
    (preferredGroup?.latestPerformanceDate ?? null) !== null;

  if (!hasPerformanceData) {
    // The organization has no performance data.
    // We can say with some certainty that they have no investments.
    // Show the empty state message.
    return (
      <DashboardPageContent>
        <MarginTop marginTop={96}>
          <WithGap gap="96">
            <NoInvestmentsMessage />

            <NewsArticlesWidget />
          </WithGap>
        </MarginTop>
      </DashboardPageContent>
    );
  }

  return (
    <DashboardPageContent>
      <PortfolioDashboard
        preferredPortfolioGroupId={preferredPortfolioGroupId}
      />
    </DashboardPageContent>
  );
};

// eslint-disable-next-line react/prop-types
const DashboardPageContent: React.FC<PropsWithChildren> = ({ children }) => {
  const organizationCmId = useOrganizationCmId();
  const { organizationName } = useOrganizationName(organizationCmId);

  return (
    <CenteredPageContent data-test="dashboard-page">
      <AnnouncementWarning />
      <OrderToSignAlert />
      <AnnouncementInfo />

      <MainContent>
        <MarginTop marginTop={24}>
          <PageHeading header={prettifyOrganizationName(organizationName)} />
        </MarginTop>

        {children}
      </MainContent>
    </CenteredPageContent>
  );
};

interface MarginTopProps {
  readonly marginTop?: number;
}

const MarginTop = styled.div<MarginTopProps>`
  margin-top: ${(props) => (props.marginTop ? `${props.marginTop}px` : '0')};
`;
